/**
 * Catálogos de textos de cría, generados desde Tambo-Back `docs/cria`:
 * - MOTIVOS: METRICAS_Y_RANKINGS.md §5 (enum `Motivo`, 69 códigos).
 * - MOTIVOS_EXCLUSION: METRICAS_Y_RANKINGS.md §6.4 (enum `MotivoExclusion`).
 * - INCIDENCIAS_CALIDAD: OPERACION_Y_CALIDAD.md §5 (enum `CodigoIncidencia`).
 *
 * Un test verifica que cubren todos los valores de `openapi_cria.json`. Los códigos nuevos
 * que agregue el backend se muestran con un texto genérico (contrato aditivo, §13.3).
 */

/** estado: no_aplica | insuficiente | inconsistente | provisional | estimado | informativo */
export const MOTIVOS = Object.freeze({
  menos_de_dos_partos: {
    estado: "no_aplica",
    texto: "Necesita al menos dos partos para calcular el intervalo.",
  },
  sin_partos: {
    estado: "no_aplica",
    texto: "Todavía no tiene partos registrados.",
  },
  sin_parto_previo: {
    estado: "no_aplica",
    texto: "No hay un parto anterior para medir días abiertos.",
  },
  ciclo_abierto: {
    estado: "no_aplica",
    texto: "Ciclo en curso: todavía no hay preñez para medir.",
  },
  preniez_activa: { estado: "no_aplica", texto: "Tiene una preñez activa." },
  destete_posterior_al_corte: {
    estado: "no_aplica",
    texto: "El destete es posterior a la fecha de corte.",
  },
  sin_vientres_expuestos: {
    estado: "no_aplica",
    texto: "La campaña no tiene vientres expuestos.",
  },
  menos_de_dos_intervalos: {
    estado: "insuficiente",
    texto: "Hace falta más de un intervalo para medir la variabilidad.",
  },
  sin_intervalos_validos: {
    estado: "insuficiente",
    texto: "No hay intervalos entre partos válidos.",
  },
  concepcion_desconocida: {
    estado: "insuficiente",
    texto: "No hay datos para estimar la concepción.",
  },
  sin_ciclos_evaluables: {
    estado: "insuficiente",
    texto: "No hay ciclos cerrados con concepción conocida.",
  },
  denominador_cero: {
    estado: "insuficiente",
    texto: "No hay casos para calcular el porcentaje.",
  },
  sin_peso_destete: {
    estado: "insuficiente",
    texto: "Falta el peso al destete.",
  },
  pesos_incompletos: {
    estado: "insuficiente",
    texto: "Faltan pesos al destete: el total no está completo.",
  },
  sin_campanias_evaluables: {
    estado: "insuficiente",
    texto: "No hay campañas evaluables.",
  },
  superficie_ausente: {
    estado: "insuficiente",
    texto: "Falta registrar la superficie de cría.",
  },
  sin_superficie_valida: {
    estado: "insuficiente",
    texto: "No hay una superficie válida para el período.",
  },
  ganancia_no_disponible: {
    estado: "insuficiente",
    texto: "Ganancia de peso no disponible.",
  },
  iep_menor_al_minimo: {
    estado: "inconsistente",
    texto: "Intervalo biológicamente imposible: revisar las fechas de parto.",
  },
  intervalo_inconsistente_excluido: {
    estado: "inconsistente",
    texto: "Se excluyó un intervalo inconsistente.",
  },
  diagnostico_contradictorio: {
    estado: "inconsistente",
    texto: "Diagnósticos contradictorios el mismo día.",
  },
  concepcion_contradictoria: {
    estado: "inconsistente",
    texto: "Las fechas de concepción posibles no coinciden.",
  },
  concepcion_anterior_al_parto_previo: {
    estado: "inconsistente",
    texto: "La concepción calculada es anterior al parto previo.",
  },
  gestacion_incompatible: {
    estado: "inconsistente",
    texto: "La gestación resultante no es posible.",
  },
  parto_esperado_no_registrado: {
    estado: "inconsistente",
    texto: "Debió parir y no hay parto registrado.",
  },
  peso_invalido: {
    estado: "inconsistente",
    texto: "Peso al destete inválido.",
  },
  cria_duplicada_con_pesos_distintos: {
    estado: "inconsistente",
    texto: "La cría tiene pesos al destete contradictorios.",
  },
  cria_atribuida_a_varias_madres: {
    estado: "inconsistente",
    texto: "La cría figura con más de una madre.",
  },
  superficie_no_positiva: {
    estado: "inconsistente",
    texto: "Superficie con hectáreas inválidas.",
  },
  segmentos_superpuestos: {
    estado: "inconsistente",
    texto: "Hay segmentos de superficie superpuestos.",
  },
  segmento_fechas_invertidas: {
    estado: "inconsistente",
    texto: "Un segmento de superficie tiene fechas invertidas.",
  },
  periodo_superficie_no_coincide: {
    estado: "inconsistente",
    texto: "El período de la superficie no coincide con la campaña.",
  },
  hijos_exceden_padre: {
    estado: "inconsistente",
    texto: "Las parcelas suman más que su lote padre.",
  },
  jerarquia_ciclica: {
    estado: "inconsistente",
    texto: "La jerarquía de lotes es circular.",
  },
  preniez_en_curso: { estado: "provisional", texto: "Preñez en curso." },
  diagnostico_no_concluyente: {
    estado: "provisional",
    texto: "Diagnóstico no concluyente (dudoso o pendiente).",
  },
  campania_abierta: {
    estado: "provisional",
    texto: "Campaña abierta: resultado provisional.",
  },
  diagnostico_incompleto: {
    estado: "provisional",
    texto: "No se diagnosticó a todos los vientres.",
  },
  ventana_de_pesaje_abierta: {
    estado: "provisional",
    texto: "Puede faltar el pesaje del destete reciente.",
  },
  superficie_con_huecos: {
    estado: "provisional",
    texto: "La superficie no cubre todo el período.",
  },
  concepcion_estimada: {
    estado: "estimado",
    texto: "Concepción estimada (± margen).",
  },
  diagnostico_fuera_de_ventana: {
    estado: "estimado",
    texto: "Diagnóstico fuera de la ventana esperada.",
  },
  diagnostico_fecha_no_confiable: {
    estado: "estimado",
    texto: "Preñez confirmada sin fecha confiable.",
  },
  fecha_destete_desconocida: {
    estado: "estimado",
    texto: "Fecha de destete estimada.",
  },
  peso_aproximado: {
    estado: "estimado",
    texto: "Peso aproximado (pesaje cercano al destete).",
  },
  posible_campania_perdida: {
    estado: "informativo",
    texto: "Intervalo > 700 días: posible campaña perdida.",
  },
  partos_misma_fecha_colapsados: {
    estado: "informativo",
    texto: "Partos de la misma fecha unificados (mellizos o duplicados).",
  },
  parto_sintetico_excluido: {
    estado: "informativo",
    texto: "Parto generado por un alta, excluido de los cálculos.",
  },
  diagnostico_igual_a_parto: {
    estado: "informativo",
    texto: "Diagnóstico en la fecha de un parto (ignorado).",
  },
  servicio_posterior_a_positivo: {
    estado: "informativo",
    texto: "Servicio registrado después de un positivo.",
  },
  servicio_no_compatible_con_parto: {
    estado: "informativo",
    texto: "Ningún servicio compatible con el parto: la concepción se estima.",
  },
  ciclos_excluidos_del_promedio: {
    estado: "informativo",
    texto: "Hay ciclos fuera del promedio (inconsistentes o sin datos).",
  },
  campania_rectificada: {
    estado: "informativo",
    texto: "La campaña fue reabierta y cerrada de nuevo.",
  },
  diagnosticos_duplicados_colapsados: {
    estado: "informativo",
    texto: "Diagnósticos repetidos unificados.",
  },
  diagnostico_fuera_de_campania: {
    estado: "informativo",
    texto: "Diagnóstico que no corresponde a la campaña.",
  },
  perdida_posterior_a_positivo: {
    estado: "informativo",
    texto: "Pérdida después de un positivo (cuenta en la merma).",
  },
  ingreso_tardio: {
    estado: "informativo",
    texto: "Vientre incorporado después del inicio del servicio.",
  },
  egreso_conservado: {
    estado: "informativo",
    texto: "Egreso por motivo reproductivo: sigue en el denominador.",
  },
  cria_no_atribuida: {
    estado: "informativo",
    texto: "Cría que no se atribuye a la madre.",
  },
  peso_corregido: {
    estado: "informativo",
    texto: "El peso vigente reemplaza a otro anterior.",
  },
  pesaje_de_otro_animal_ignorado: {
    estado: "informativo",
    texto: "Pesaje de otro animal descartado.",
  },
  pesaje_posterior_al_corte_ignorado: {
    estado: "informativo",
    texto: "Pesaje posterior al corte descartado.",
  },
  pesaje_fuera_de_tolerancia: {
    estado: "informativo",
    texto: "Pesaje fuera de ±7 días descartado.",
  },
  cria_duplicada_colapsada: {
    estado: "informativo",
    texto: "Cría repetida unificada.",
  },
  cria_de_madre_no_expuesta_excluida: {
    estado: "informativo",
    texto: "Cría de una madre no expuesta: fuera del total.",
  },
  campania_no_evaluable_excluida: {
    estado: "informativo",
    texto: "Campaña no evaluable excluida del cálculo individual.",
  },
  lote_no_cria_excluido: {
    estado: "informativo",
    texto: "Lote que no es de cría excluido de la superficie.",
  },
  lote_padre_contado_por_sus_hijos: {
    estado: "informativo",
    texto: "Lote padre contado a través de sus parcelas.",
  },
  superficie_remanente_de_padre_no_contada: {
    estado: "informativo",
    texto: "Parte del lote padre no cubierta por parcelas, no contada.",
  },
});

export const MOTIVOS_EXCLUSION = Object.freeze({
  campania_duplicada_contradictoria:
    "Tiene datos duplicados y distintos en una campaña: revisar la inscripción.",
  datos_campania_contradictorios:
    "Hay datos contradictorios en una campaña del período.",
  campanias_evaluables_insuficientes:
    "Tiene menos campañas evaluables que las necesarias para comparar.",
  sin_partos_en_horizonte: "No tuvo partos en las campañas del período.",
  dias_desde_inicio_paricion_faltantes:
    "Falta la fecha de parto respecto del inicio de parición.",
  sin_iep_valido: "No tiene un intervalo entre partos válido en el período.",
  sin_dias_abiertos_comparables:
    "Sin días abiertos comparables (concepción solo estimada). Ver la variante sin días abiertos.",
  pesos_incompletos:
    "Falta el peso al destete de algún ternero: el resultado está incompleto.",
  regularidad_insuficiente:
    "Necesita al menos dos partos para medir la regularidad.",
});

/** severidad: bloqueante | advertencia | info. `correccion`: acción autorizada que lo corrige. */
export const INCIDENCIAS_CALIDAD = Object.freeze({
  caravana_duplicada: {
    severidad: "bloqueante",
    significado:
      "La misma caravana en varios animales vivos del establecimiento (`detalle.animal_ids`)",
    correccion:
      "Corregir la caravana en la ficha del animal (edición existente de animales)",
  },
  confirmacion_antes_de_servicio: {
    severidad: "bloqueante",
    significado: "Confirmación de preñez anterior al servicio",
    correccion:
      "Sin ruta de edición: corregir en origen con soporte; la lectura descarta esa confirmación",
  },
  fecha_anterior_al_nacimiento: {
    severidad: "bloqueante",
    significado:
      "Un evento con fecha anterior al nacimiento del animal (`detalle.campo`)",
    correccion:
      "Corregir la fecha de nacimiento en la ficha o el evento en origen",
  },
  fecha_posterior_al_corte: {
    severidad: "bloqueante",
    significado: "Un evento con fecha posterior a la fecha de referencia",
    correccion: "Corregir la fecha del evento en origen",
  },
  cantidad_partos_discrepante: {
    severidad: "advertencia",
    significado:
      "El contador de partos del animal no coincide con los partos registrados",
    correccion:
      "Registrar los partos faltantes o corregir el contador en la ficha; los indicadores usan los partos registrados",
  },
  parto_duplicado: {
    severidad: "advertencia",
    significado: "Dos partos de la misma madre en la misma fecha",
    correccion:
      "Los indicadores los colapsan (`partos_misma_fecha_colapsados`); borrar el duplicado requiere soporte",
  },
  confirmacion_igual_a_parto: {
    severidad: "advertencia",
    significado: "Confirmación cerrada por el parto, sin diagnóstico real",
    correccion: "Informativo; registrar tactos en las próximas campañas",
  },
  confirmada_sin_resultado: {
    severidad: "advertencia",
    significado: "Servicio confirmado sin resultado (confirmación legacy)",
    correccion:
      "Registrar un tacto con `resultado` y `fuente_diagnostico` (`POST /cria/registrar-tacto/{animal_id}`)",
  },
  cria_sin_madre: {
    severidad: "advertencia",
    significado:
      "La caravana de la madre de la cría no existe entre las hembras",
    correccion:
      "Sin ruta para asignar madre a una cría existente: backfill o soporte; la cría no se atribuye",
  },
  cria_madre_ambigua: {
    severidad: "advertencia",
    significado:
      "La caravana de la madre corresponde a varias hembras (`detalle.candidatas`)",
    correccion:
      "Resolver la caravana duplicada; el backfill la reporta como ambigua y no la vincula",
  },
  destete_sin_fecha: {
    severidad: "advertencia",
    significado: "Evento de destete sin `fecha_destete`",
    correccion:
      "Los indicadores lo tratan como fecha estimada. `POST /feedlot/destete/{animal_id}` acepta un animal sin `fecha_destete` pero crea un **evento nuevo** (⚠ confirmar con el responsable antes de usarlo así)",
  },
  destete_sin_peso: {
    severidad: "advertencia",
    significado: "Sin pesaje dentro de 15 días del destete",
    correccion: "`POST /cria/animales/{animal_id}/peso-destete`",
  },
  destete_peso_copiado: {
    severidad: "advertencia",
    significado:
      "El único peso cercano es la copia de `peso_actual`, no una pesada",
    correccion:
      "`POST /cria/animales/{animal_id}/peso-destete` con el peso real",
  },
  parto_sintetico: {
    severidad: "info",
    significado: "Parto generado al dar de alta o importar el animal",
    correccion: "Informativo: se excluye del IEP (`parto_sintetico_excluido`)",
  },
  servicio_a_campo_estimado: {
    severidad: "info",
    significado: "Servicio inventado por un tacto positivo sin servicio",
    correccion: "Informativo: nunca se usa como concepción",
  },
  inseminacion_ficticia_probable: {
    severidad: "info",
    significado:
      "Servicio sin datos confirmado un número fijo de días después: probablemente creado por la confirmación manual de preñez",
    correccion: "Informativo: no se usa como concepción",
  },
});
